import React from 'react';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';

import { LomavisOverlayTemplateCard } from '@/lomavisSharedUi/molecules/cards/LomavisOverlayTemplateCard';
import {
  LomavisTemplateFolderSidebar,
  LomavisTemplateFolderSidebarLabels
} from '@/lomavisSharedUi/organisms/navigation/LomavisTemplateFolderSidebar';
import {
  OverlayTemplateCardLabels,
  OverlayTemplateFolder,
  OverlayTemplateLibraryMode,
  OverlayTemplateSortOption,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisOverlayTemplateLibraryLabels {
  title: string;
  description: string;
  back: string;
  createTemplate: string;
  addFromAccountLibrary: string;
  search: string;
  sort: string;
  emptyTitle: string;
  emptyDescription: string;
  retry: string;
  loading: string;
  templateCount: (count: number) => string;
  folderSidebar: LomavisTemplateFolderSidebarLabels;
  card: OverlayTemplateCardLabels;
}

export interface LomavisOverlayTemplateLibraryProps {
  mode: OverlayTemplateLibraryMode;
  templates: OverlayTemplateSummary[];
  folders: OverlayTemplateFolder[];
  selectedFolderId: string | null;
  searchValue: string;
  sortValue: OverlayTemplateSortValue;
  sortOptions: OverlayTemplateSortOption[];
  labels: LomavisOverlayTemplateLibraryLabels;
  showPageHeading?: boolean;
  showBackAction?: boolean;
  showCreateAction?: boolean;
  showAccountLibraryAction?: boolean;
  canManageFolders?: boolean;
  isLoading?: boolean;
  isFolderLoading?: boolean;
  errorMessage?: string | null;
  onBack?: () => void;
  onCreateTemplate?: () => void;
  onOpenAccountLibrary?: () => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: OverlayTemplateSortValue) => void;
  onFolderSelect: (folderId: string | null) => void;
  onCreateFolder?: () => void;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
  onEditTemplate?: (templateId: string) => void;
  onChangeTemplateFolder?: (templateId: string) => void;
  onDuplicateTemplate?: (templateId: string) => void;
  onCloneTemplate?: (templateId: string) => void;
  onDeleteTemplate?: (templateId: string) => void;
  onStartPost?: (templateId: string) => void;
  onRetry?: () => void;
}

const TemplateCardSkeleton: React.FC<{ loadingLabel: string }> = ({
  loadingLabel
}) => (
  <div className="card h-100 border bg-white overflow-hidden placeholder-wave">
    <div className="ratio ratio-1x1 bg-light">
      <span className="placeholder w-100 h-100 bg-secondary bg-opacity-10" />
      <span className="visually-hidden">{loadingLabel}</span>
    </div>
    <div className="card-body p-3">
      <span className="placeholder col-8 mb-2 rounded bg-secondary bg-opacity-25" />
      <span className="placeholder col-4 rounded bg-secondary bg-opacity-25" />
    </div>
  </div>
);

/**
 * Template: shared overlay-template library layout for all three product modes.
 */
export const LomavisOverlayTemplateLibrary: React.FC<
  LomavisOverlayTemplateLibraryProps
> = ({
  mode,
  templates,
  folders,
  selectedFolderId,
  searchValue,
  sortValue,
  sortOptions,
  labels,
  showPageHeading = true,
  showBackAction = false,
  showCreateAction = false,
  showAccountLibraryAction = false,
  canManageFolders = false,
  isLoading = false,
  isFolderLoading = isLoading,
  errorMessage,
  onBack,
  onCreateTemplate,
  onOpenAccountLibrary,
  onSearchChange,
  onSortChange,
  onFolderSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onEditTemplate,
  onChangeTemplateFolder,
  onDuplicateTemplate,
  onCloneTemplate,
  onDeleteTemplate,
  onStartPost,
  onRetry
}) => {
  return (
    <section
      className="container-fluid bg-light py-4 px-3 px-lg-4 h-100 d-flex flex-column gap-3 overflow-hidden"
      data-library-mode={mode}
      aria-label={labels.title}
    >
      {showPageHeading && (
        <header className="flex-shrink-0">
          <h1 className="fs-2 fw-bold text-dark mb-1">{labels.title}</h1>
          <p className="text-muted mb-0">{labels.description}</p>
        </header>
      )}
      <div className="card border shadow-sm bg-white overflow-auto w-100 flex-grow-1">
        <div className="row g-0 align-items-stretch h-100">
          <div className="col-12 col-lg-3 col-xl-2 border-end h-lg-100 overflow-hidden">
            <LomavisTemplateFolderSidebar
              folders={folders}
              selectedFolderId={selectedFolderId}
              labels={labels.folderSidebar}
              canManageFolders={canManageFolders}
              isLoading={isFolderLoading}
              onFolderSelect={onFolderSelect}
              onCreateFolder={onCreateFolder}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
            />
          </div>

          <main className="col-12 col-lg-9 col-xl-10 bg-white d-flex flex-column overflow-hidden h-lg-100">
            <header className="border-bottom p-3">
              <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-2">
                {showBackAction && onBack && (
                  <Button
                    type="button"
                    size="sm"
                    variant="light"
                    className="btn-icon flex-shrink-0"
                    aria-label={labels.back}
                    onClick={onBack}
                  >
                    <i
                      className="fa-regular fa-arrow-left"
                      aria-hidden="true"
                    />
                  </Button>
                )}

                <InputGroup size="sm" className="flex-grow-1 w-100">
                  <InputGroup.Text className="bg-white border-end-0">
                    <i
                      className="fa-regular fa-magnifying-glass text-muted"
                      aria-hidden="true"
                    />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    aria-label={labels.search}
                    placeholder={labels.search}
                    value={searchValue}
                    disabled={isLoading}
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                </InputGroup>

                {!errorMessage && (
                  <span className="text-muted fw-semibold text-nowrap">
                    {labels.templateCount(templates.length)}
                  </span>
                )}

                <Form.Select
                  size="sm"
                  className="w-auto flex-shrink-0"
                  aria-label={labels.sort}
                  value={sortValue}
                  disabled={isLoading}
                  onChange={(event) =>
                    onSortChange(event.target.value as OverlayTemplateSortValue)
                  }
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <div className="d-flex flex-wrap flex-sm-nowrap gap-2">
                  {showAccountLibraryAction && onOpenAccountLibrary && (
                    <Button
                      type="button"
                      size="sm"
                      variant="light"
                      className="text-nowrap"
                      onClick={onOpenAccountLibrary}
                    >
                      <i
                        className="fa-regular fa-building me-2"
                        aria-hidden="true"
                      />
                      {labels.addFromAccountLibrary}
                    </Button>
                  )}
                  {showCreateAction && onCreateTemplate && (
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      className="text-nowrap"
                      onClick={onCreateTemplate}
                    >
                      <i
                        className="fa-regular fa-plus me-2"
                        aria-hidden="true"
                      />
                      {labels.createTemplate}
                    </Button>
                  )}
                </div>
              </div>
              {!showPageHeading && (
                <p className="visually-hidden">{labels.description}</p>
              )}
            </header>

            <div className="p-4 flex-grow-1 overflow-auto">
              {errorMessage && (
                <Alert
                  variant="danger"
                  className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3"
                >
                  <span>{errorMessage}</span>
                  {onRetry && (
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      onClick={onRetry}
                    >
                      {labels.retry}
                    </Button>
                  )}
                </Alert>
              )}

              {!errorMessage && (
                <>
                  {isLoading && (
                    <div className="row g-3" aria-label={labels.loading}>
                      {[0, 1, 2, 3, 4, 5].map((position) => (
                        <div
                          className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                          key={position}
                        >
                          <TemplateCardSkeleton loadingLabel={labels.loading} />
                        </div>
                      ))}
                    </div>
                  )}

                  {!isLoading && templates.length === 0 && (
                    <div className="d-flex h-100 flex-column align-items-center justify-content-center text-center px-4">
                      <span className="symbol symbol-70px bg-light-primary rounded-circle mb-4 d-inline-flex align-items-center justify-content-center">
                        <i
                          className="fa-regular fa-layer-group fs-1 text-primary"
                          aria-hidden="true"
                        />
                      </span>
                      <h2 className="fs-4 fw-bold text-dark mb-2">
                        {labels.emptyTitle}
                      </h2>
                      <p className="text-muted mb-4">
                        {labels.emptyDescription}
                      </p>
                      {showCreateAction && onCreateTemplate && (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          onClick={onCreateTemplate}
                        >
                          {labels.createTemplate}
                        </Button>
                      )}
                    </div>
                  )}

                  {!isLoading && templates.length > 0 && (
                    <div className="row g-3">
                      {templates.map((template) => (
                        <div
                          className="col-12 col-sm-6 col-lg-4 col-xxl-3"
                          key={template.id}
                        >
                          <LomavisOverlayTemplateCard
                            template={template}
                            labels={labels.card}
                            showAspectRatioOnPreview={mode === 'accountPicker'}
                            onEdit={onEditTemplate}
                            onChangeFolder={onChangeTemplateFolder}
                            onDuplicate={onDuplicateTemplate}
                            onClone={onCloneTemplate}
                            onDelete={onDeleteTemplate}
                            onStartPost={onStartPost}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </section>
  );
};
